import React, { useState, useEffect } from 'react';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import MapView from '../Map/Map';
import BackToTop from '../Back-to-top/BackToTop';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../Firebase/Config';
import { useCompany } from '../../Contexts/CompanyContext';

interface ContactFormData {
    name: string;
    email: string;
    phone: string;
    whatsapp: string;
    message: string;
}

interface ToastProps {
    type: 'success' | 'error';
    message: string;
    onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ type, message, onClose }) => {
    useEffect(() => {
        const timer = setTimeout(onClose, 5000);
        return () => clearTimeout(timer);
    }, [onClose]);

    return (
        <div
            style={{
                position: 'fixed',
                bottom: '30px',
                right: '30px',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                minWidth: '280px',
                maxWidth: '400px',
                padding: '14px 18px',
                borderRadius: '8px',
                backgroundColor: type === 'success' ? '#28a745' : '#dc3545',
                color: '#fff',
                boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                animation: 'slideInUp 0.3s ease',
            }}
        >
            <span style={{ fontSize: '20px' }}>
                {type === 'success' ? '✓' : '✕'}
            </span>
            <span style={{ flex: 1, fontSize: '14px', lineHeight: '1.4' }}>
                {message}
            </span>
            <button
                onClick={onClose}
                style={{
                    background: 'none',
                    border: 'none',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '18px',
                    lineHeight: 1,
                    padding: '0',
                    opacity: 0.8,
                }}
                aria-label="Close notification"
            >
                &times;
            </button>
            <style>{`
                @keyframes slideInUp {
                    from { transform: translateY(30px); opacity: 0; }
                    to   { transform: translateY(0);   opacity: 1; }
                }
            `}</style>
        </div>
    );
};

// ── Inline SVG Icons ──────────────────────────────────────────────────────────

const PersonIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
    </svg>
);

const EmailIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
);

const PhoneIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.62 3.4 2 2 0 0 1 3.59 1.22h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.78a16 16 0 0 0 5.89 5.89l1.61-1.62a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
);

const WhatsAppIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="#25D366">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
    </svg>
);

// ─────────────────────────────────────────────────────────────────────────────

const inputIconStyle: React.CSSProperties = {
    position: 'absolute',
    top: '50%',
    left: '18px',
    transform: 'translateY(-50%)',
    color: '#999',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
};

const Contact: React.FC = () => {
    const { companyProfile, location, loading } = useCompany();

    const [formData, setFormData] = useState<ContactFormData>({
        name: '',
        email: '',
        phone: '',
        whatsapp: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [toast, setToast] = useState<{
        type: 'success' | 'error';
        message: string;
    } | null>(null);

    const closeToast = () => setToast(null);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
            setToast({ type: 'error', message: 'Please fill in all required fields' });
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setToast({ type: 'error', message: 'Please enter a valid email address' });
            return;
        }

        const phoneRegex = /^[+]?[\d\s\-().]{7,15}$/;
        if (formData.phone.trim() && !phoneRegex.test(formData.phone)) {
            setToast({ type: 'error', message: 'Please enter a valid contact number' });
            return;
        }

        if (formData.whatsapp.trim() && !phoneRegex.test(formData.whatsapp)) {
            setToast({ type: 'error', message: 'Please enter a valid WhatsApp number' });
            return;
        }

        setIsSubmitting(true);

        try {
            await addDoc(collection(db, 'contactMessages'), {
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                whatsapp: formData.whatsapp,
                message: formData.message,
                createdAt: Timestamp.now(),
                status: 'unread'
            });

            setToast({ type: 'success', message: 'Thank you! Your message has been sent successfully.' });
            setFormData({ name: '', email: '', phone: '', whatsapp: '', message: '' });
        } catch (error) {
            console.error('Error saving contact message:', error);
            setToast({ type: 'error', message: 'Failed to send message. Please try again.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div>
            <Header />

            {/* Toast Notification */}
            {toast && (
                <Toast
                    type={toast.type}
                    message={toast.message}
                    onClose={closeToast}
                />
            )}

            <section className="bg-img1 txt-center p-lr-15 p-tb-92" style={{ backgroundImage: `url('asset/images/bg-11.png')` }}>
                <h2 className="ltext-105 cl0 txt-center">
                    Contact
                </h2>
            </section>

            <section className="bg0 p-t-104 p-b-116">
                <div className="container">
                    <div className="flex-w flex-tr">
                        {/* Contact Form */}
                        <div className="size-210 bor10 p-lr-70 p-t-55 p-b-70 p-lr-15-lg w-full-md">
                            <form onSubmit={handleSubmit}>
                                <h4 className="mtext-105 cl2 txt-center p-b-30">
                                    Send Us A Message
                                </h4>

                                {/* Name */}
                                <div className="bor8 m-b-20 how-pos4-parent">
                                    <input
                                        className="stext-111 cl2 plh3 size-116 p-l-62 p-r-30"
                                        type="text"
                                        name="name"
                                        placeholder="Your Full Name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                        disabled={isSubmitting}
                                    />
                                    <span className="how-pos4 pointer-none" style={inputIconStyle}>
                                        <PersonIcon />
                                    </span>
                                </div>

                                {/* Email */}
                                <div className="bor8 m-b-20 how-pos4-parent">
                                    <input
                                        className="stext-111 cl2 plh3 size-116 p-l-62 p-r-30"
                                        type="email"
                                        name="email"
                                        placeholder="Your Email Address"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        required
                                        disabled={isSubmitting}
                                    />
                                    <span className="how-pos4 pointer-none" style={inputIconStyle}>
                                        <EmailIcon />
                                    </span>
                                </div>

                                {/* Contact Number */}
                                <div className="bor8 m-b-20 how-pos4-parent">
                                    <input
                                        className="stext-111 cl2 plh3 size-116 p-l-62 p-r-30"
                                        type="tel"
                                        name="phone"
                                        placeholder="Your Contact Number"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        disabled={isSubmitting}
                                    />
                                    <span className="how-pos4 pointer-none" style={inputIconStyle}>
                                        <PhoneIcon />
                                    </span>
                                </div>

                                {/* WhatsApp Number */}
                                <div className="bor8 m-b-20 how-pos4-parent">
                                    <input
                                        className="stext-111 cl2 plh3 size-116 p-l-62 p-r-30"
                                        type="tel"
                                        name="whatsapp"
                                        placeholder="Your WhatsApp Number"
                                        value={formData.whatsapp}
                                        onChange={handleInputChange}
                                        disabled={isSubmitting}
                                    />
                                    <span className="how-pos4 pointer-none" style={inputIconStyle}>
                                        <WhatsAppIcon />
                                    </span>
                                </div>

                                {/* Message */}
                                <div className="bor8 m-b-30">
                                    <textarea
                                        className="stext-111 cl2 plh3 size-120 p-lr-28 p-tb-25"
                                        name="message"
                                        placeholder="How Can We Help?"
                                        value={formData.message}
                                        onChange={handleInputChange}
                                        required
                                        disabled={isSubmitting}
                                    ></textarea>
                                </div>

                                <button
                                    type="submit"
                                    className="flex-c-m stext-101 cl0 size-121 bg3 bor1 hov-btn3 p-lr-15 trans-04 pointer"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Sending...' : 'Submit'}
                                </button>
                            </form>
                        </div>

                        {/* Contact Info */}
                        <div className="size-210 bor10 flex-w flex-col-m p-lr-93 p-tb-30 p-lr-15-lg w-full-md">
                            {loading ? (
                                <div className="txt-center w-full p-tb-30">
                                    <p className="stext-111 cl2">Loading contact information...</p>
                                </div>
                            ) : (
                                <>
                                    {/* Address */}
                                    <div className="flex-w w-full p-b-42">
                                        <span className="fs-18 cl5 txt-center size-211">
                                            <span className="lnr lnr-map-marker"></span>
                                        </span>
                                        <div className="size-212 p-t-2">
                                            <span className="mtext-110 cl2">Address</span>
                                            <p className="stext-115 cl6 size-213 p-t-18">
                                                {location?.address || 'Address not available'}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Phone */}
                                    <div className="flex-w w-full p-b-42">
                                        <span className="fs-18 cl5 txt-center size-211">
                                            <span className="lnr lnr-phone-handset"></span>
                                        </span>
                                        <div className="size-212 p-t-2">
                                            <span className="mtext-110 cl2">Let's Talk</span>
                                            <p className="stext-115 cl1 size-213 p-t-18">
                                                {companyProfile?.contactInfo.phone}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Email */}
                                    <div className="flex-w w-full p-b-42">
                                        <span className="fs-18 cl5 txt-center size-211">
                                            <span className="lnr lnr-envelope"></span>
                                        </span>
                                        <div className="size-212 p-t-2">
                                            <span className="mtext-110 cl2">Sale Support</span>
                                            <p className="stext-115 cl1 size-213 p-t-18">
                                                {companyProfile?.contactInfo.email}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Website (optional) */}
                                    {companyProfile?.contactInfo.website && (
                                        <div className="flex-w w-full p-t-42">
                                            <span className="fs-18 cl5 txt-center size-211">
                                                <span className="lnr lnr-earth"></span>
                                            </span>
                                            <div className="size-212 p-t-2">
                                                <span className="mtext-110 cl2">Website</span>
                                                <p className="stext-115 cl1 size-213 p-t-18">
                                                    <a
                                                        href={companyProfile.contactInfo.website}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="cl1 hov-cl1"
                                                    >
                                                        {companyProfile.contactInfo.website}
                                                    </a>
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            <MapView />
            <Footer />
            <BackToTop />
        </div>
    );
};

export default Contact;